import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Calculator, TrendingUp, Info, Download, Target, Share2, BookOpen } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const SASUDirectorSalarySimulator = () => {
  const [inputs, setInputs] = useState({
    chiffreAffaires: '100000',
    salaireBrutMensuel: 3000,
    dividendesAnnuels: 20000,
    fraisProfessionnels: '5000'
  });

  const [results, setResults] = useState({});

  const calculateSASU = () => {
    const chiffreAffaires = parseFloat(inputs.chiffreAffaires) || 0;
    const salaireBrutMensuel = inputs.salaireBrutMensuel;
    const dividendesAnnuels = inputs.dividendesAnnuels;
    const fraisProfessionnels = parseFloat(inputs.fraisProfessionnels) || 0;
    
    const salaireBrutAnnuel = salaireBrutMensuel * 12;
    
    // Charges patronales (environ 42%)
    const chargesPatronales = salaireBrutAnnuel * 0.42;
    
    // Charges salariales (environ 22%)
    const chargesSalariales = salaireBrutAnnuel * 0.22;
    
    // Salaire net
    const salaireNetAnnuel = salaireBrutAnnuel - chargesSalariales;
    const salaireNetMensuel = salaireNetAnnuel / 12;
    
    // Coût total du salaire pour l'entreprise
    const coutTotalSalaire = salaireBrutAnnuel + chargesPatronales;
    
    // Résultat avant IS
    const resultatAvantIS = chiffreAffaires - coutTotalSalaire - fraisProfessionnels;
    
    // Impôt sur les sociétés
    let impotSurSocietes = 0;
    if (resultatAvantIS > 0) {
      // Taux réduit sur les premiers 42 500€
      const baseIS_15 = Math.min(resultatAvantIS, 42500);
      const baseIS_25 = Math.max(resultatAvantIS - 42500, 0);
      
      impotSurSocietes = (baseIS_15 * 0.15) + (baseIS_25 * 0.25);
    }
    
    // Résultat net après IS
    const resultatNetApreIS = resultatAvantIS - impotSurSocietes;
    
    // Dividendes et flat tax
    const flatTaxDividendes = dividendesAnnuels * 0.30; // 30% flat tax
    
    // Revenus nets du dirigeant
    const dividendesNets = dividendesAnnuels - flatTaxDividendes;
    const revenusNetsTotal = salaireNetAnnuel + dividendesNets;
    const revenusNetsMensuel = revenusNetsTotal / 12;
    
    // Calcul de l'optimisation dynamique
    // On teste différents scénarios pour trouver le meilleur équilibre salaire/dividendes
    const PASS_2026 = 46368;
    
    // Scénario 1: Salaire au PASS (optimal pour la retraite)
    const salaireOptimalBrut = Math.min(PASS_2026, chiffreAffaires * 0.5); // Ne pas dépasser 50% du CA
    const chargesPatronalesOpt = salaireOptimalBrut * 0.42;
    const chargesSalarialesOpt = salaireOptimalBrut * 0.22;
    const salaireNetOpt = salaireOptimalBrut - chargesSalarialesOpt;
    const coutTotalSalaireOpt = salaireOptimalBrut + chargesPatronalesOpt;
    
    const resultatAvantISOpt = chiffreAffaires - coutTotalSalaireOpt - fraisProfessionnels;
    let impotSocietesOpt = 0;
    if (resultatAvantISOpt > 0) {
      const baseIS_15_opt = Math.min(resultatAvantISOpt, 42500);
      const baseIS_25_opt = Math.max(resultatAvantISOpt - 42500, 0);
      impotSocietesOpt = (baseIS_15_opt * 0.15) + (baseIS_25_opt * 0.25);
    }
    
    const resultatNetOpt = Math.max(0, resultatAvantISOpt - impotSocietesOpt);
    const dividendesNetsOpt = resultatNetOpt * 0.70; // Après flat tax 30%
    const revenusOptimauxTotal = salaireNetOpt + dividendesNetsOpt;
    
    // Différence avec la situation actuelle
    const gainOptimisation = revenusOptimauxTotal - revenusNetsTotal;
    const optimisationPossible = gainOptimisation > 500; // Seuil de 500€ pour suggérer
    
    // Taux global d'imposition
    const chargesETImpotTotal = chargesPatronales + chargesSalariales + impotSurSocietes + flatTaxDividendes;
    const tauxImposition = chiffreAffaires > 0 ? (chargesETImpotTotal / chiffreAffaires) * 100 : 0;
    
    setResults({
      salaireBrutAnnuel,
      salaireBrutMensuel,
      chargesPatronales,
      chargesSalariales,
      salaireNetAnnuel,
      salaireNetMensuel,
      coutTotalSalaire,
      resultatAvantIS,
      impotSurSocietes,
      resultatNetApreIS,
      dividendesAnnuels,
      flatTaxDividendes,
      dividendesNets,
      revenusNetsTotal,
      revenusNetsMensuel,
      tauxImposition,
      // Données optimisation
      salaireOptimalBrut: Math.round(salaireOptimalBrut),
      salaireOptimalNet: Math.round(salaireNetOpt),
      dividendesOptimaux: Math.round(resultatNetOpt),
      dividendesNetsOptimaux: Math.round(dividendesNetsOpt),
      revenusOptimauxTotal: Math.round(revenusOptimauxTotal),
      revenusOptimauxMensuel: Math.round(revenusOptimauxTotal / 12),
      gainOptimisation: Math.round(gainOptimisation),
      optimisationPossible
    });
  };

  useEffect(() => {
    calculateSASU();
  }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSliderChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value[0]
    }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: 'Simulateur Salaire Dirigeant SASU - Bozzam',
        text: 'Calculez votre rémunération optimale en SASU',
        url: url
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('Lien copié dans le presse-papier !');
    }
  };

  const exportResults = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175); doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(20);
    doc.text('Bozzam - Salaire Dirigeant SASU', 15, 25);
    doc.setFontSize(10); doc.text('Simulation réalisée sur blog.bozzam.com', 15, 35);
    doc.setTextColor(0, 0, 0); let y = 55;
    doc.setFontSize(14); doc.text('Paramètres', 15, y); y += 10;
    doc.setFontSize(11);
    doc.text(`CA annuel HT : ${parseInt(inputs.chiffreAffaires).toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Salaire brut mensuel : ${inputs.salaireBrutMensuel.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Dividendes annuels : ${inputs.dividendesAnnuels.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Frais professionnels : ${parseInt(inputs.fraisProfessionnels).toLocaleString()} EUR`, 15, y); y += 15;
    doc.setFontSize(14); doc.text('Résultats', 15, y); y += 10;
    doc.setFontSize(11);
    const lines = [
      [`Salaire net mensuel`, `${results.salaireNetMensuel?.toLocaleString()} EUR`],
      [`Salaire net annuel`, `${results.salaireNetAnnuel?.toLocaleString()} EUR`],
      [`Coût total salaire`, `${results.coutTotalSalaire?.toLocaleString()} EUR`],
      [`IS`, `${results.impotSurSocietes?.toLocaleString()} EUR`],
      [`Dividendes nets`, `${results.dividendesNets?.toLocaleString()} EUR`],
      [`Revenus nets totaux`, `${results.revenusNetsTotal?.toLocaleString()} EUR`],
      [`Taux imposition global`, `${results.tauxImposition?.toFixed(1)}%`],
    ];
    lines.forEach(([l, v]) => { doc.text(`${l} : ${v}`, 15, y); y += 7; });
    if (results.optimisationPossible) {
      y += 5;
      doc.setFontSize(12); doc.setTextColor(200, 100, 0);
      doc.text(`Optimisation possible : +${results.gainOptimisation?.toLocaleString()} EUR/an`, 15, y); y += 7;
      doc.setTextColor(0); doc.setFontSize(11);
      doc.text(`Salaire optimal : ${results.salaireOptimalBrut?.toLocaleString()} EUR brut/an`, 15, y); y += 7;
      doc.text(`Revenus optimisés : ${results.revenusOptimauxTotal?.toLocaleString()} EUR/an`, 15, y); y += 7;
    }
    y += 10;
    doc.setFontSize(9); doc.setTextColor(100, 100, 100);
    doc.text('Sources : CGI art. 219, URSSAF, PASS 2026 46 368 EUR, Flat tax CGI art. 200 A', 15, y);
    doc.text('Consultez un expert-comptable pour votre situation personnelle.', 15, y + 5);
    doc.save('simulation-salaire-dirigeant-sasu-bozzam.pdf');
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Formulaire */}
        <Card className="xl:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" />
              Paramètres SASU
            </CardTitle>
            <CardDescription>
              Optimisez votre rémunération salaire + dividendes
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="ca">Chiffre d'affaires annuel HT</Label>
              <Input
                id="ca"
                type="text"
                inputMode="numeric"
                value={inputs.chiffreAffaires}
                onChange={(e) => handleInputChange('chiffreAffaires', e.target.value.replace(/[^0-9]/g, ''))}
                className="mt-1"
                placeholder="100000"
              />
            </div>
            
            <div>
              <Label htmlFor="salaire">
                Salaire brut mensuel: {inputs.salaireBrutMensuel.toLocaleString()}€
              </Label>
              <Slider
                value={[inputs.salaireBrutMensuel]}
                onValueChange={(value) => handleSliderChange('salaireBrutMensuel', value)}
                max={8000}
                min={1000}
                step={100}
                className="mt-3"
              />
            </div>
            
            <div>
              <Label htmlFor="dividendes">
                Dividendes annuels: {inputs.dividendesAnnuels.toLocaleString()}€
              </Label>
              <Slider
                value={[inputs.dividendesAnnuels]}
                onValueChange={(value) => handleSliderChange('dividendesAnnuels', value)}
                max={50000}
                min={0}
                step={1000}
                className="mt-3"
              />
            </div>
            
            <div>
              <Label htmlFor="frais">Frais professionnels annuels</Label>
              <Input
                id="frais"
                type="text"
                inputMode="numeric"
                value={inputs.fraisProfessionnels}
                onChange={(e) => handleInputChange('fraisProfessionnels', e.target.value.replace(/[^0-9]/g, ''))}
                className="mt-1"
                placeholder="5000"
              />
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources légales 2026</p>
                  <p>Code général des impôts art. 219 • PASS URSSAF 46 368€ • Flat tax CGI art. 200 A.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Résultats détaillés */}
        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Analyse détaillée de votre rémunération
            </CardTitle>
            <div className="flex gap-2">
              <Badge className="bg-blue-100 text-blue-800">
                Taux global: {results.tauxImposition?.toFixed(1)}%
              </Badge>
              {results.optimisationPossible && (
                <Badge className="bg-orange-100 text-orange-800">
                  <Target className="h-3 w-3 mr-1" />
                  Optimisation possible
                </Badge>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Salaire */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">💼 Rémunération salaire</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs font-medium text-blue-600 mb-1">Salaire brut</p>
                  <p className="text-lg font-bold text-blue-900">
                    {results.salaireBrutAnnuel?.toLocaleString()}€
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xs font-medium text-red-600 mb-1">Charges patronales</p>
                  <p className="text-lg font-bold text-red-900">
                    -{results.chargesPatronales?.toLocaleString()}€
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xs font-medium text-red-600 mb-1">Charges salariales</p>
                  <p className="text-lg font-bold text-red-900">
                    -{results.chargesSalariales?.toLocaleString()}€
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border-2 border-green-200">
                  <p className="text-xs font-medium text-green-600 mb-1">Salaire net</p>
                  <p className="text-lg font-bold text-green-900">
                    {results.salaireNetAnnuel?.toLocaleString()}€
                  </p>
                </div>
              </div>
            </div>
            
            {/* Société */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">🏢 Niveau société</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs font-medium text-blue-600 mb-1">CA annuel</p>
                  <p className="text-lg font-bold text-blue-900">
                    {parseInt(inputs.chiffreAffaires).toLocaleString()}€
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xs font-medium text-red-600 mb-1">Coût total salaire</p>
                  <p className="text-lg font-bold text-red-900">
                    -{results.coutTotalSalaire?.toLocaleString()}€
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xs font-medium text-red-600 mb-1">Impôt sociétés</p>
                  <p className="text-lg font-bold text-red-900">
                    -{results.impotSurSocietes?.toLocaleString()}€
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-600 mb-1">Résultat net</p>
                  <p className="text-lg font-bold text-gray-900">
                    {results.resultatNetApreIS?.toLocaleString()}€
                  </p>
                </div>
              </div>
            </div>
            
            {/* Dividendes */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">💰 Dividendes</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs font-medium text-purple-600 mb-1">Dividendes bruts</p>
                  <p className="text-lg font-bold text-purple-900">
                    {results.dividendesAnnuels?.toLocaleString()}€
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xs font-medium text-red-600 mb-1">Flat tax (30%)</p>
                  <p className="text-lg font-bold text-red-900">
                    -{results.flatTaxDividendes?.toLocaleString()}€
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border-2 border-green-200">
                  <p className="text-xs font-medium text-green-600 mb-1">Dividendes nets</p>
                  <p className="text-lg font-bold text-green-900">
                    {results.dividendesNets?.toLocaleString()}€
                  </p>
                </div>
              </div>
            </div>
            
            {/* Total */}
            <div className="p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200">
              <div className="text-center">
                <h4 className="font-bold text-gray-900 mb-2">🎯 Revenus nets totaux du dirigeant</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Annuel</p>
                    <p className="text-3xl font-black text-green-900">
                      {results.revenusNetsTotal?.toLocaleString()}€
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Mensuel</p>
                    <p className="text-3xl font-black text-green-900">
                      {results.revenusNetsMensuel?.toLocaleString()}€
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Conseil optimisation */}
            {results.optimisationPossible && (
              <div className="p-5 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border-2 border-orange-200">
                <div className="flex items-start gap-3">
                  <Target className="h-6 w-6 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-bold text-orange-800 mb-3 text-lg">
                      💡 Optimisation possible : +{results.gainOptimisation?.toLocaleString()}€/an
                    </p>
                    
                    <div className="bg-white rounded-lg p-4 mb-3">
                      <p className="font-semibold text-gray-800 mb-2">Scénario optimisé suggéré :</p>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-gray-600">Salaire brut annuel</p>
                          <p className="font-bold text-blue-700">{results.salaireOptimalBrut?.toLocaleString()}€</p>
                          <p className="text-xs text-gray-500">({Math.round(results.salaireOptimalBrut / 12)?.toLocaleString()}€/mois)</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Salaire net annuel</p>
                          <p className="font-bold text-green-700">{results.salaireOptimalNet?.toLocaleString()}€</p>
                          <p className="text-xs text-gray-500">({Math.round(results.salaireOptimalNet / 12)?.toLocaleString()}€/mois)</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Dividendes bruts</p>
                          <p className="font-bold text-purple-700">{results.dividendesOptimaux?.toLocaleString()}€</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Dividendes nets</p>
                          <p className="font-bold text-green-700">{results.dividendesNetsOptimaux?.toLocaleString()}€</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between bg-green-100 rounded-lg p-3">
                      <div>
                        <p className="text-sm text-green-800">Revenus nets totaux optimisés</p>
                        <p className="font-bold text-green-900 text-xl">{results.revenusOptimauxTotal?.toLocaleString()}€/an</p>
                        <p className="text-xs text-green-700">soit {results.revenusOptimauxMensuel?.toLocaleString()}€/mois</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-green-800">vs actuel</p>
                        <p className="font-bold text-orange-700">+{results.gainOptimisation?.toLocaleString()}€</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className="grid grid-cols-3 gap-3">
              <Button onClick={shareLink} variant="outline">
                <Share2 className="h-4 w-4 mr-2" />
                Partager
              </Button>
              
              <Button onClick={exportResults} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Exporter
              </Button>
              
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}
              >
                Bozzam App
              </Button>
            </div>
            
            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              <p>Calculs basés sur : Code général des impôts, URSSAF, PASS 2026</p>
              <p>Sources officielles • Consultez un expert-comptable pour conseil personnalisé</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SASUDirectorSalarySimulator;